import {
  selectCreatingConsultation,
  selectCurrentConsultation,
  selectLastConsultation,
  selectConsultationPatient,
  selectConsultationPetOwner,
  selectSkipProductBook,
  selectExpectedNextVisit,
  selectVetSourceOrderStatus,
  selectIsWLPIncluded,
  selectIsWLPEligible,
} from '@app/store/consultation/consultation.selectors';
import { Injectable } from '@angular/core';
import {
  resetConsultationData,
  setConsultationPetOwner,
  getExpectedNextVisit,
  setExpectedNextVisit,
  resetExpectedNextVisit,
  setConsultationPatient,
  setConsultationLastConsultation,
  setCurrentConsultationVisitDate,
  setConsultationSetSkipProductBook,
  createCurrentConsultationFail,
  setIsWLPIncluded,
  setIsWLPEligible,
} from '@app/store/consultation/consultation.actions';
import { Store } from '@ngrx/store';
import { AppState } from '..';
import { Consultation, Patient, PetInfo, PetOwner } from '@app/core/models';
import { ReproductionStatusCode, Tool } from '@app/shared/utils';
import { NextVisitData } from '@app/core/models/visit-next';
import { filter, map, of, switchMap } from 'rxjs';
import { selectToolInfo } from '../core';
import { selectProfile } from '../patient-profile/patient-profile.selectors';

@Injectable()
export class ConsultationFacade {
  patient$ = this.store$.select(selectConsultationPatient);
  petOwner$ = this.store$.select(selectConsultationPetOwner);
  lastConsultation$ = this.store$.select(selectLastConsultation);
  currentConsultation$ = this.store$.select(selectCurrentConsultation);
  creatingConsultation$ = this.store$.select(selectCreatingConsultation);
  expectedNextVisit$ = this.store$.select(selectExpectedNextVisit);
  skipProductBook$ = this.store$.select(selectSkipProductBook);
  vetSourceOrderStatus$ = this.store$.select(selectVetSourceOrderStatus);
  isWLPEligible$ = this.store$.select(selectIsWLPEligible);
  isWLPIncluded$ = this.store$.select(selectIsWLPIncluded);

  private readonly profileDependsOnTool$ = this.store$.select(selectToolInfo).pipe(
    switchMap(({ tool }) => {
      switch (tool) {
        case Tool.SmartReco:
        case Tool.Rationing:
        case Tool.RenalDetect:
          return this.store$.select(selectProfile);
        default:
          return of(null);
      }
    }),
  );
  reproductionStatus$ = this.profileDependsOnTool$.pipe(map((profile) => profile?.reproductionStatus));
  isLactation$ = this.reproductionStatus$.pipe(map((reproductionStatus) => reproductionStatus === ReproductionStatusCode.Lactation));
  speciesCode$ = this.profileDependsOnTool$.pipe(
    filter((profile) => !!profile),
    map((value) => value.speciesCode),
  );

  constructor(private readonly store$: Store<AppState>) {}

  /** consultation.actions */
  setCurrentConsultationVisitDate(date: Date): void {
    this.store$.dispatch(setCurrentConsultationVisitDate({ value: date }));
  }

  setConsultationPatient(patient: Patient): void {
    this.store$.dispatch(setConsultationPatient({ value: patient }));
  }

  setConsultationLastConsultation(lastConsultation: Consultation): void {
    this.store$.dispatch(setConsultationLastConsultation({ value: lastConsultation }));
  }

  setConsultationSetSkipProductBook(skip: boolean): void {
    this.store$.dispatch(setConsultationSetSkipProductBook({ value: skip }));
  }

  resetConsultationData(): void {
    this.store$.dispatch(resetConsultationData());
  }

  createCurrentConsultationFail(error: string): void {
    this.store$.dispatch(createCurrentConsultationFail({ error }));
  }

  setConsultationPetOwner(value: PetOwner): void {
    this.store$.dispatch(setConsultationPetOwner({ value }));
  }

  getExpectedNextVisit(value: PetInfo, nextDate?: Date): void {
    this.store$.dispatch(getExpectedNextVisit({ value, nextDate }));
  }

  setExpectedNextVisit(value: NextVisitData): void {
    this.store$.dispatch(setExpectedNextVisit({ value }));
  }

  resetExpectedNextVisit(): void {
    this.store$.dispatch(resetExpectedNextVisit());
  }

  setIsWLPEligible(value: boolean): void {
    this.store$.dispatch(setIsWLPEligible({ value }));
  }

  setIsWLPincluded(value: boolean): void {
    this.store$.dispatch(setIsWLPIncluded({ value }));
  }
}
